/**
 * Progression dans un chapitre : quand une étape est-elle terminée ?
 *
 * Sert au fil d'Ariane de l'interface, qui verrouille l'étape suivante tant que
 * l'actuelle n'est pas finie. La règle dépend de la `mecanique` de l'étape,
 * déclarée dans le contenu — pas de son numéro.
 */
import { chainEquipmentIds } from './chapter2.js';
import { programmeModifie } from './chapter3.js';
import { semaineOrganisee } from './chapter4.js';
import type { Content, Step } from './content-schema.js';
import { getEligibleEvents } from './events.js';
import type { GameState } from './state.js';
import { currentChapter } from './state.js';

/**
 * Nombre de problèmes à repérer avant de pouvoir quitter l'étape « Explorer ».
 *
 * Vient de la consigne du contenu (« Repérez au moins 6 problèmes ») et du plan.
 * Le chiffre est ici plutôt que dans le JSON parce que l'ajouter au contenu
 * suppose une évolution du schéma, donc l'accord de l'enseignant.
 */

/**
 * L'étape est-elle terminée ?
 *
 * Les mécaniques du chapitre 2 non encore implémentées (l'observation guidée
 * attend ses trois questions dans le contenu) ne bloquent pas la progression :
 * mieux vaut une classe qui avance qu'une classe coincée.
 */
export function isStepComplete(state: GameState, content: Content, step: Step): boolean {
  switch (step.mecanique) {
    case 'exploration_carte':
      return state.discoveredProblems.length >= content.reglages.problemes_a_reperer_minimum;

    case 'tri_categories': {
      // Ranger ne suffit pas : il faut avoir fait corriger, et soit avoir juste,
      // soit avoir épuisé ses secondes chances.
      const problemesParId = new Map(content.problemes.map((p) => [p.id, p]));

      return (
        state.discoveredProblems.length > 0 &&
        state.discoveredProblems.every((problemId) => {
          const tentatives = state.classificationAttempts[problemId] ?? 0;
          if (tentatives === 0) {
            return false;
          }
          const attendue = problemesParId.get(problemId)?.categorie;
          return (
            state.classification[problemId] === attendue ||
            tentatives > content.reglages.secondes_chances
          );
        })
      );
    }

    case 'choix_oui_partiel_non':
      return (
        state.discoveredProblems.length > 0 &&
        state.discoveredProblems.every(
          (problemId) => state.connectedAnalysis[problemId] !== undefined,
        )
      );

    case 'priorisation_top3':
      return state.priorities.length === content.reglages.places_au_podium;

    case 'glisser_deposer_chaine':
      // Une chaîne posée suffit, même si elle est fausse : c'est à l'étape
      // suivante, en simulant la nuit, que l'îlot découvre ce qui cloche. Exiger
      // ici la bonne solution enfermerait un groupe dans l'écran de conception,
      // sans jamais lui montrer pourquoi la sienne ne marche pas.
      return chainEquipmentIds(state.chain).length > 0;

    case 'simulation_nuit':
      return state.deployedSolutionId !== null;

    case 'evenement':
      return getEligibleEvents(state, content, state.chapterId).length === 0;

    case 'reparer_programme':
      // Avoir touché au programme suffit : c'est en simulant la semaine que
      // l'îlot découvre ce qui cloche. Exiger ici le bon réglage l'enfermerait
      // dans l'écran d'édition sans jamais lui montrer pourquoi le sien rate.
      return programmeModifie(state, content);

    case 'simulation_semaine':
      // Il faut avoir lancé la semaine au moins une fois, donc avoir un
      // programme enregistré à éprouver.
      return state.programme !== null;

    case 'organiser_tournee':
      // Chaque jour de sortie doit avoir été traité — y compris en décidant que
      // le camion ne sort pas. C'est la décision qui compte, pas son contenu.
      return semaineOrganisee(state, content);

    case 'bilan_tournees':
      // Le bilan se lit, il ne se valide pas : avoir organisé la semaine suffit.
      return semaineOrganisee(state, content);

    case 'observation_guidee': {
      // Terminée quand toutes les questions guidées du chapitre ont une réponse.
      // Un chapitre sans question ne bloque personne.
      const questions = currentChapter(state, content).questions_guidees ?? [];
      return questions.every((question) => state.guidedAnswers[question.id] !== undefined);
    }

    default:
      return true;
  }
}

/** L'étape courante est-elle terminée ? */
export function isCurrentStepComplete(state: GameState, content: Content): boolean {
  const etape = currentChapter(state, content).deroule[state.stepIndex];
  return etape ? isStepComplete(state, content, etape) : false;
}

/**
 * Jusqu'où l'îlot a-t-il le droit d'aller ? Une étape est accessible si toutes
 * celles qui la précèdent sont terminées.
 */
export function maxReachableStepIndex(state: GameState, content: Content): number {
  const deroule = currentChapter(state, content).deroule;

  for (let index = 0; index < deroule.length; index += 1) {
    const etape = deroule[index];
    if (!etape || !isStepComplete(state, content, etape)) {
      return index;
    }
  }
  return deroule.length - 1;
}

/** L'étape d'indice donné est-elle déverrouillée ? */
export function isStepUnlocked(state: GameState, content: Content, stepIndex: number): boolean {
  return stepIndex <= maxReachableStepIndex(state, content);
}
